import type { AgreementSettingsValue } from '../config/instance-settings-registry';
import { canonicalFormJson } from './form-canonical-json';
import { FORM_DEFAULT_TODAY } from './form-defaults';
import { FORM_SIGNATURE_DATA_URL_PREFIX } from './form-signature';
import {
	DEFAULT_FORM_SEND_SETTINGS,
	FORM_CALCULATIONS,
	FORM_COMPUTED_CHOICES,
	FORM_FIELD_CUSTOMER_ACCESS,
	FORM_PAYMENT_METHOD_KINDS,
	FORM_PROCESSING_PROFILES,
	FORM_SIGNATURE_MODES,
	FORM_TABLE_COLUMN_KINDS,
	FORM_TABLE_ROW_SOURCES,
	type FormAnswerValue,
	type FormFieldCustomerAccess,
	type FormFieldDefinitionModel,
	type FormFieldOptionsModel,
	type FormSendSettingsModel,
	type FormTableColumnModel,
	type FormTableRowModel,
} from './form-types';

/**
 * PD-13768 — sent forms (docs/claude/forms/04-sent-forms.md): a form staff send to a
 * known client, which the customer opens through a no-login link, completes and signs.
 *
 * Pure and isomorphic, like the rest of Workd Forms, so the CRM's send dialog, the
 * storefront page and crm-api apply the same rules. The server is the authority: it builds
 * every value the customer may not change itself (mergeSentFormAnswers), whatever the page
 * sends.
 */

// ─── Value sources and write-back targets ───────────────────────────────────

/** The sender types the value before sending. Always locked for the customer. */
export const FORM_STAFF_SOURCE = 'staff';

/** Set by the server to the day the customer signs. Always locked. */
export const FORM_SIGNING_DATE_SOURCE = 'signing.date';

/** A client custom field, `customField:<key>`, as a source or a write-back target. */
export const SENT_FORM_CUSTOM_FIELD_PREFIX = 'customField:';

export type SentFormSourceGroup =
	| 'Client'
	| 'Service address'
	| 'Billing'
	| 'Contact'
	| 'Delivery'
	| 'Other';

/**
 * One place a sent form's value can come from (and, when `writable`, go back to). The
 * service address is the client's ship-to address; the billing one its bill-to address.
 */
export interface SentFormSourceModel {
	id: string;
	label: string;
	group: SentFormSourceGroup;
	/** Whether a customer's change can be proposed for it. */
	writable: boolean;
}

export const SENT_FORM_SOURCES: ReadonlyArray<SentFormSourceModel> = [
	{ id: 'client.name', label: 'Business name', group: 'Client', writable: true },
	{ id: 'client.legalName', label: 'Legal name', group: 'Client', writable: true },
	{ id: 'client.accountNumber', label: 'Customer number', group: 'Client', writable: false },
	{ id: 'client.phone', label: 'Phone', group: 'Client', writable: true },
	{ id: 'client.email', label: 'Email', group: 'Client', writable: true },
	{ id: 'client.businessType', label: 'Business type', group: 'Client', writable: true },
	{ id: 'client.hours', label: 'Hours of operation', group: 'Client', writable: true },
	{ id: 'client.note', label: 'Client note (write-back only)', group: 'Client', writable: true },
	{ id: 'shipping.address1', label: 'Address', group: 'Service address', writable: true },
	{ id: 'shipping.address2', label: 'Address line 2', group: 'Service address', writable: true },
	{ id: 'shipping.city', label: 'City', group: 'Service address', writable: true },
	{ id: 'shipping.state', label: 'State', group: 'Service address', writable: true },
	{ id: 'shipping.zip', label: 'ZIP code', group: 'Service address', writable: true },
	{ id: 'shipping.county', label: 'County', group: 'Service address', writable: true },
	{ id: 'billing.name', label: 'Billing name', group: 'Billing', writable: true },
	{ id: 'billing.address1', label: 'Billing address', group: 'Billing', writable: true },
	{ id: 'billing.address2', label: 'Billing address line 2', group: 'Billing', writable: true },
	{ id: 'billing.city', label: 'Billing city', group: 'Billing', writable: true },
	{ id: 'billing.state', label: 'Billing state', group: 'Billing', writable: true },
	{ id: 'billing.zip', label: 'Billing ZIP code', group: 'Billing', writable: true },
	{ id: 'billing.phone', label: 'Billing phone', group: 'Billing', writable: true },
	{ id: 'contact.name', label: 'Contact name', group: 'Contact', writable: true },
	{ id: 'contact.title', label: 'Contact title', group: 'Contact', writable: true },
	{ id: 'contact.email', label: 'Contact email', group: 'Contact', writable: true },
	{ id: 'contact.phone', label: 'Contact phone', group: 'Contact', writable: true },
	{
		id: 'client.requiresDeliverySignature',
		label: 'Signature required at delivery',
		group: 'Delivery',
		writable: true,
	},
	{
		id: 'client.deliveryTiming',
		label: 'Delivery timing (24 hours, day only, night only)',
		group: 'Delivery',
		writable: true,
	},
	{ id: 'client.billingTerms', label: 'Payment timing and terms', group: 'Other', writable: true },
	{ id: FORM_STAFF_SOURCE, label: 'Filled by staff when sending', group: 'Other', writable: false },
	{ id: FORM_SIGNING_DATE_SOURCE, label: 'The day the customer signs', group: 'Other', writable: false },
];

const SOURCE_BY_ID = new Map(SENT_FORM_SOURCES.map((source) => [source.id, source]));

/** Whether an id names a known source (a catalog entry or a custom field). */
export function isSentFormSource(id: string): boolean {
	return (
		SOURCE_BY_ID.has(id) ||
		(id.startsWith(SENT_FORM_CUSTOM_FIELD_PREFIX) &&
			id.length > SENT_FORM_CUSTOM_FIELD_PREFIX.length)
	);
}

/** Whether an id names a target a customer's change can be proposed for. */
export function isSentFormWriteBackTarget(id: string): boolean {
	if (id.startsWith(SENT_FORM_CUSTOM_FIELD_PREFIX)) {
		return id.length > SENT_FORM_CUSTOM_FIELD_PREFIX.length;
	}
	return SOURCE_BY_ID.get(id)?.writable === true;
}

export function sentFormSourceLabel(id: string): string {
	if (id.startsWith(SENT_FORM_CUSTOM_FIELD_PREFIX)) {
		return `Custom field ${id.slice(SENT_FORM_CUSTOM_FIELD_PREFIX.length)}`;
	}
	return SOURCE_BY_ID.get(id)?.label ?? id;
}

// ─── Customer access ───────────────────────────────────────────────────────

/** Whether staff fill the field before sending (and so it is always locked). */
export function isStaffFilledField(
	field: Pick<FormFieldDefinitionModel, 'valueSource' | 'fieldType'>,
): boolean {
	// A table's rows are always the sender's (equipment, products, prices).
	return field.valueSource === FORM_STAFF_SOURCE || field.fieldType === 'table';
}

/**
 * What the customer may do with a field. A staff-filled field, a table, a value the server
 * sets at signing, and a calculated value are locked whatever the setting says; a missing
 * setting reads as `fill`.
 */
export function effectiveCustomerAccess(
	field: Pick<
		FormFieldDefinitionModel,
		'valueSource' | 'customerAccess' | 'fieldType' | 'fieldOptions'
	>,
): FormFieldCustomerAccess {
	if (field.customerAccess === 'internal') {
		return 'internal';
	}
	if (
		isStaffFilledField(field) ||
		field.valueSource === FORM_SIGNING_DATE_SOURCE ||
		field.fieldOptions?.calculation != null
	) {
		return 'locked';
	}
	const access = field.customerAccess ?? 'fill';
	return (FORM_FIELD_CUSTOMER_ACCESS as ReadonlyArray<string>).includes(access)
		? access
		: 'fill';
}

/** The customer's own input decides the value: they fill it or may change it. */
export function isCustomerWritable(
	field: Parameters<typeof effectiveCustomerAccess>[0],
): boolean {
	const access = effectiveCustomerAccess(field);
	return access === 'fill' || access === 'editable';
}

/**
 * The fields a customer sees, in order: everything but the internal ones. A locked field
 * is shown read-only; a consent or signature is always the customer's.
 */
export function customerVisibleFields<
	TFieldModel extends Parameters<typeof effectiveCustomerAccess>[0],
>(fields: ReadonlyArray<TFieldModel>): Array<TFieldModel> {
	return fields.filter((field) => effectiveCustomerAccess(field) !== 'internal');
}

// ─── Merging what the customer sent with what the server holds ────────────

export interface MergeSentFormAnswersInputModel {
	fields: ReadonlyArray<FormFieldDefinitionModel>;
	/** Values read from the client when the form was sent. */
	prefilled: Readonly<Record<string, FormAnswerValue>>;
	/** Values the sender typed. */
	staff: Readonly<Record<string, FormAnswerValue>>;
	/** What the page sent; never trusted for a locked, staff or internal field. */
	customer: unknown;
	/** `YYYY-MM-DD`, the day the customer signs (in the tenant's time zone). */
	signingDate: string;
}

export interface MergeSentFormAnswersResultModel {
	/** The raw answers to validate: the customer's where they may write, else the server's. */
	raw: Record<string, unknown>;
	/**
	 * Fields the page sent a different value for although the customer may not change them.
	 * The value is ignored either way; the list is logged so a tampered page is visible.
	 */
	ignoredKeys: Array<string>;
}

/**
 * PD-13768 — the answers of a sent form as the server accepts them. For a field the
 * customer fills or may change, the page's value; for every other field (locked, staff,
 * internal, a table, the signing date, a calculation) the value the server holds, so a
 * hand-made request cannot change a price, an equipment row or the agreed terms.
 */
export function mergeSentFormAnswers(
	input: MergeSentFormAnswersInputModel,
): MergeSentFormAnswersResultModel {
	const customer: Record<string, unknown> =
		input.customer != null &&
		typeof input.customer === 'object' &&
		!Array.isArray(input.customer)
			? (input.customer as Record<string, unknown>)
			: {};
	const has = (record: object, key: string): boolean =>
		Object.prototype.hasOwnProperty.call(record, key);

	const raw: Record<string, unknown> = {};
	const ignoredKeys: Array<string> = [];
	for (const field of input.fields) {
		const key = field.fieldKey;
		const serverValue = serverHeldValue(field, input);
		if (isCustomerWritable(field)) {
			if (has(customer, key)) {
				raw[key] = customer[key];
			} else if (serverValue !== undefined) {
				raw[key] = serverValue;
			}
			continue;
		}
		if (serverValue !== undefined) {
			raw[key] = serverValue;
		}
		if (
			has(customer, key) &&
			!sameAnswer(customer[key], serverValue)
		) {
			ignoredKeys.push(key);
		}
	}
	return { raw, ignoredKeys };
}

function serverHeldValue(
	field: FormFieldDefinitionModel,
	input: MergeSentFormAnswersInputModel,
): FormAnswerValue | undefined {
	if (field.valueSource === FORM_SIGNING_DATE_SOURCE) {
		return input.signingDate;
	}
	const key = field.fieldKey;
	if (Object.prototype.hasOwnProperty.call(input.staff, key)) {
		return input.staff[key];
	}
	if (Object.prototype.hasOwnProperty.call(input.prefilled, key)) {
		return input.prefilled[key];
	}
	return undefined;
}

/** Two answers compared by value (a table's rows, a list of choices). */
export function sameAnswer(a: unknown, b: unknown): boolean {
	const normalize = (value: unknown): unknown =>
		value === '' || value === undefined ? null : value;
	return canonicalFormJson(normalize(a)) === canonicalFormJson(normalize(b));
}

// ─── Proposed changes ──────────────────────────────────────────────────────

export interface SentFormProposedChangeDraftModel {
	fieldKey: string;
	label: string;
	target: string;
	from: string;
	to: string;
}

/**
 * PD-13768 — the changes a customer's answers propose to the client record: every field
 * that writes back, which the customer may change, whose answer differs from the value
 * read from the client when the form was sent. Staff accept or reject each when they
 * process the submission; nothing is written before that.
 *
 * Text is compared trimmed and case-sensitively: a corrected capital is a change worth
 * proposing; surrounding spaces are not.
 */
export function sentFormProposedChanges(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	prefilled: Readonly<Record<string, FormAnswerValue>>,
	answers: Readonly<Record<string, FormAnswerValue>>,
	display: (field: FormFieldDefinitionModel, value: FormAnswerValue | undefined) => string,
): Array<SentFormProposedChangeDraftModel> {
	const changes: Array<SentFormProposedChangeDraftModel> = [];
	for (const field of fields) {
		const target = field.writeBackTarget ?? null;
		if (target == null || !isCustomerWritable(field)) {
			continue;
		}
		const before = prefilled[field.fieldKey];
		const after = answers[field.fieldKey];
		const from = display(field, before).trim();
		const to = display(field, after).trim();
		if (from === to) {
			continue;
		}
		changes.push({
			fieldKey: field.fieldKey,
			label: field.adminLabel ?? field.label,
			target,
			from,
			to,
		});
	}
	return changes;
}

// ─── Signing fingerprint ───────────────────────────────────────────────────

export interface SentFormSignedContentInputModel {
	formName: string;
	formVersion: number;
	/** Every field shown, with its value as rendered, in form order. */
	shown: ReadonlyArray<{ fieldKey: string; label: string; display: string }>;
	/** Each terms statement's full text (as stored, sanitized HTML). */
	terms: ReadonlyArray<{ fieldKey: string; label: string; body: string | null }>;
	/** Display text blocks (notices) as shown. */
	texts: ReadonlyArray<string>;
	signerName: string;
	signerTitle: string | null;
	signedAt: string;
}

/**
 * PD-13768 — exactly what a signer was shown, as one canonical string. Its SHA-256 is the
 * signing record's fingerprint (hashed in crm-api, which has Node's crypto); computing it
 * again from the stored submission and version shows whether the record still matches.
 */
export function sentFormSignedContent(
	input: SentFormSignedContentInputModel,
): string {
	return canonicalFormJson({
		form: input.formName,
		version: input.formVersion,
		shown: input.shown.map((item) => [item.fieldKey, item.label, item.display]),
		terms: input.terms.map((item) => [item.fieldKey, item.label, item.body ?? '']),
		texts: [...input.texts],
		signer: [input.signerName, input.signerTitle ?? '', input.signedAt],
	});
}

// ─── Payment method answers ────────────────────────────────────────────────

/** The answer of a payment method question when staff chose Bill To terms and none was given. */
export const FORM_PAYMENT_BILL_TO = 'bill_to';

const PAYMENT_ANSWER_PATTERN = /^method:(\d{1,10})$/;

export function formPaymentMethodAnswer(paymentMethodId: number): string {
	return `method:${String(paymentMethodId)}`;
}

/** The saved method id an answer names, or null (Bill To, empty, malformed). */
export function parseFormPaymentMethodAnswer(value: unknown): number | null {
	if (typeof value !== 'string') {
		return null;
	}
	const match = PAYMENT_ANSWER_PATTERN.exec(value);
	return match != null ? Number(match[1]) : null;
}

export function isFormPaymentAnswer(value: string): boolean {
	return value === FORM_PAYMENT_BILL_TO || PAYMENT_ANSWER_PATTERN.test(value);
}

// ─── Billing terms (payment timing and terms) ──────────────────────────────

/**
 * PD-13768 — the payment timing and terms a sender agrees with the customer, as one
 * choice. The form only records them; processing writes them to the client (the Customer
 * Agreement) or to the event's orders (the Event Form). It never bills anything.
 *
 * Values: `charge_on_delivery`, `pay_at_order`, `bill_on_terms:<termsCodeId>`,
 * `cycle_billing:<weekly|biweekly|monthly>`; with billing modes off, `charge_on_delivery`
 * or `invoice_terms:<termsCodeId>` (invoiced, paid directly: Bill To).
 */
export type FormBillingTermsModel =
	| { kind: 'charge_on_delivery' }
	| { kind: 'pay_at_order' }
	| { kind: 'bill_on_terms'; termsCodeId: number }
	| { kind: 'cycle_billing'; frequency: 'weekly' | 'biweekly' | 'monthly' }
	| { kind: 'invoice_terms'; termsCodeId: number };

export function encodeFormBillingTerms(terms: FormBillingTermsModel): string {
	switch (terms.kind) {
		case 'bill_on_terms':
		case 'invoice_terms':
			return `${terms.kind}:${String(terms.termsCodeId)}`;
		case 'cycle_billing':
			return `cycle_billing:${terms.frequency}`;
		default:
			return terms.kind;
	}
}

export function parseFormBillingTerms(value: unknown): FormBillingTermsModel | null {
	if (typeof value !== 'string') {
		return null;
	}
	if (value === 'charge_on_delivery' || value === 'pay_at_order') {
		return { kind: value };
	}
	const [kind, rest] = value.split(':');
	if ((kind === 'bill_on_terms' || kind === 'invoice_terms') && /^\d{1,10}$/.test(rest ?? '')) {
		return { kind, termsCodeId: Number(rest) };
	}
	if (
		kind === 'cycle_billing' &&
		(rest === 'weekly' || rest === 'biweekly' || rest === 'monthly')
	) {
		return { kind, frequency: rest };
	}
	return null;
}

/**
 * Whether the terms invoice the customer to pay directly ("Bill To"): the payment method
 * is then optional unless the sender asks for one anyway.
 */
export function isBillToTerms(terms: FormBillingTermsModel | null): boolean {
	return terms?.kind === 'invoice_terms' || terms?.kind === 'bill_on_terms';
}

// ─── Tables ────────────────────────────────────────────────────────────────

/** The most rows a table takes unless its settings say fewer. */
export const FORM_TABLE_MAX_ROWS = 50;

/** The longest cell. */
export const FORM_TABLE_CELL_MAX_LENGTH = 500;

/**
 * A table's rows cleaned to its columns: unknown keys dropped, cells trimmed text, number
 * and price cells plain decimals, rows with nothing in them left out. Null when the value
 * is not a list of rows or holds more rows than allowed.
 */
export function cleanFormTableRows(
	columns: ReadonlyArray<FormTableColumnModel>,
	input: unknown,
	maxRows: number = FORM_TABLE_MAX_ROWS,
): Array<FormTableRowModel> | 'invalid' | 'too_many' {
	if (!Array.isArray(input)) {
		return 'invalid';
	}
	const rows: Array<FormTableRowModel> = [];
	for (const rawRow of input) {
		if (rawRow == null || typeof rawRow !== 'object' || Array.isArray(rawRow)) {
			return 'invalid';
		}
		const record = rawRow as Record<string, unknown>;
		const row: FormTableRowModel = {};
		for (const column of columns) {
			const value = Object.prototype.hasOwnProperty.call(record, column.key)
				? record[column.key]
				: undefined;
			if (value == null || value === '') {
				continue;
			}
			const text =
				typeof value === 'number' && Number.isFinite(value)
					? String(value)
					: typeof value === 'string'
						? value.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/<[^>]*>/g, '').trim()
						: null;
			if (text == null || text.length > FORM_TABLE_CELL_MAX_LENGTH) {
				return 'invalid';
			}
			if (text === '') {
				continue;
			}
			if (
				(column.kind === 'number' || column.kind === 'currency') &&
				!/^-?\d+(\.\d+)?$/.test(text)
			) {
				return 'invalid';
			}
			if ((column.kind === 'item' || column.kind === 'model') && !/^\d{1,10}$/.test(text)) {
				return 'invalid';
			}
			row[column.key] = text;
		}
		if (Object.keys(row).length > 0) {
			rows.push(row);
		}
	}
	if (rows.length > maxRows) {
		return 'too_many';
	}
	return rows;
}

/** A table as one line of text per row ("Model 60 Outdoor Slant · 2 · 7 LB and 20 LB"). */
export function formTableText(
	rows: ReadonlyArray<FormTableRowModel>,
	columns?: ReadonlyArray<Pick<FormTableColumnModel, 'key' | 'label'>>,
	cellText?: (columnKey: string, value: string) => string,
): string {
	return rows
		.map((row) => {
			const keys = columns != null ? columns.map((column) => column.key) : Object.keys(row);
			return keys
				.flatMap((key) => {
					const value = row[key];
					if (value == null || value === '') {
						return [];
					}
					return [cellText != null ? cellText(key, value) : value];
				})
				.join(' · ');
		})
		.join('\n');
}

export function isFormTableAnswer(value: unknown): value is Array<FormTableRowModel> {
	return (
		Array.isArray(value) &&
		value.every((row) => row != null && typeof row === 'object' && !Array.isArray(row))
	);
}

// ─── Settings normalizers ──────────────────────────────────────────────────

/**
 * A field's options read into exactly the stored shape: anything unknown is dropped, so a
 * request can never store more than the model allows. What the values mean (a table with
 * no columns, a payment question with no kinds) is judged by checkSentFormField.
 */
export function normalizeFormFieldOptions(raw: unknown): FormFieldOptionsModel | null {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return null;
	}
	const input = raw as Record<string, unknown>;
	const options: FormFieldOptionsModel = {};

	if (input.table != null && typeof input.table === 'object') {
		const table = input.table as Record<string, unknown>;
		const columns = Array.isArray(table.columns)
			? table.columns
					.filter(
						(column): column is Record<string, unknown> =>
							column != null && typeof column === 'object',
					)
					.map((column) => ({
						key: typeof column.key === 'string' ? column.key.trim() : '',
						label: typeof column.label === 'string' ? column.label.trim() : '',
						kind: (FORM_TABLE_COLUMN_KINDS as ReadonlyArray<unknown>).includes(column.kind)
							? (column.kind as FormTableColumnModel['kind'])
							: 'text',
					}))
			: [];
		options.table = {
			columns,
			rowSource: (FORM_TABLE_ROW_SOURCES as ReadonlyArray<unknown>).includes(table.rowSource)
				? (table.rowSource as NonNullable<FormFieldOptionsModel['table']>['rowSource'])
				: null,
			...(Array.isArray(table.itemIds)
				? {
						itemIds: table.itemIds.filter(
							(id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0,
						),
					}
				: {}),
			...(typeof table.chargeItemId === 'number' &&
			Number.isInteger(table.chargeItemId) &&
			table.chargeItemId > 0
				? { chargeItemId: table.chargeItemId }
				: {}),
			...(typeof table.maxRows === 'number' &&
			Number.isInteger(table.maxRows) &&
			table.maxRows > 0 &&
			table.maxRows <= FORM_TABLE_MAX_ROWS
				? { maxRows: table.maxRows }
				: {}),
		};
	}
	if (input.payment != null && typeof input.payment === 'object') {
		const payment = input.payment as Record<string, unknown>;
		const kinds = Array.isArray(payment.kinds)
			? FORM_PAYMENT_METHOD_KINDS.filter((kind) =>
					(payment.kinds as Array<unknown>).includes(kind),
				)
			: [];
		options.payment = {
			kinds: kinds.length > 0 ? kinds : [...FORM_PAYMENT_METHOD_KINDS],
			allowBillTo: payment.allowBillTo === true,
		};
	}
	if (Array.isArray(input.signatureModes)) {
		const modes = FORM_SIGNATURE_MODES.filter((mode) =>
			(input.signatureModes as Array<unknown>).includes(mode),
		);
		if (modes.length > 0) {
			options.signatureModes = modes;
		}
	}
	if (input.termsDisplay === 'dialog' || input.termsDisplay === 'box') {
		options.termsDisplay = input.termsDisplay;
	}
	if ((FORM_COMPUTED_CHOICES as ReadonlyArray<unknown>).includes(input.choices)) {
		options.choices = input.choices as FormFieldOptionsModel['choices'];
	}
	if ((FORM_CALCULATIONS as ReadonlyArray<unknown>).includes(input.calculation)) {
		options.calculation = input.calculation as FormFieldOptionsModel['calculation'];
	}
	if (typeof input.role === 'string' && /^[a-zA-Z][a-zA-Z0-9_]{0,59}$/.test(input.role)) {
		options.role = input.role;
	}
	return Object.keys(options).length > 0 ? options : null;
}

/** Problems with a field's sent-form settings, as `{ path, message }` for the editor. */
export function checkSentFormField(
	field: FormFieldDefinitionModel,
	path: string,
): Array<{ path: string; message: string }> {
	const problems: Array<{ path: string; message: string }> = [];
	const options = field.fieldOptions ?? null;
	if (field.valueSource != null && !isSentFormSource(field.valueSource)) {
		problems.push({ path: `${path}.valueSource`, message: 'Unknown value source' });
	}
	if (
		field.writeBackTarget != null &&
		!isSentFormWriteBackTarget(field.writeBackTarget)
	) {
		problems.push({
			path: `${path}.writeBackTarget`,
			message: 'This field cannot be written back to',
		});
	}
	if (
		field.customerAccess != null &&
		!(FORM_FIELD_CUSTOMER_ACCESS as ReadonlyArray<string>).includes(field.customerAccess)
	) {
		problems.push({ path: `${path}.customerAccess`, message: 'Unknown customer access' });
	}
	if (field.fieldType === 'table') {
		const columns = options?.table?.columns ?? [];
		if (columns.length === 0) {
			problems.push({ path: `${path}.fieldOptions.table`, message: 'A table needs at least one column' });
		}
		const keys = new Set<string>();
		columns.forEach((column, index) => {
			if (!/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/.test(column.key) || keys.has(column.key)) {
				problems.push({
					path: `${path}.fieldOptions.table.columns[${index}].key`,
					message: 'Each column needs its own key (letters, digits, underscores)',
				});
			}
			keys.add(column.key);
			if (column.label === '' || column.label.length > 120) {
				problems.push({
					path: `${path}.fieldOptions.table.columns[${index}].label`,
					message: 'Each column needs a label of at most 120 characters',
				});
			}
		});
	} else if (options?.table != null) {
		problems.push({ path: `${path}.fieldOptions.table`, message: 'Only a table has columns' });
	}
	if (field.fieldType !== 'paymentMethod' && options?.payment != null) {
		problems.push({
			path: `${path}.fieldOptions.payment`,
			message: 'Only a payment method question has payment settings',
		});
	}
	if (options?.choices != null && field.fieldType !== 'select') {
		problems.push({
			path: `${path}.fieldOptions.choices`,
			message: 'Worked-out choices are for a dropdown question',
		});
	}
	return problems;
}

/** A form's sent-form settings with every value present and within bounds. */
export function normalizeFormSendSettings(raw: unknown): FormSendSettingsModel {
	const input =
		raw != null && typeof raw === 'object' && !Array.isArray(raw)
			? (raw as Record<string, unknown>)
			: {};
	const defaults = DEFAULT_FORM_SEND_SETTINGS;
	const expiry = input.linkExpiryDays;
	return {
		sendable: input.sendable === true,
		linkExpiryDays:
			typeof expiry === 'number' && Number.isInteger(expiry) && expiry >= 1 && expiry <= 365
				? expiry
				: defaults.linkExpiryDays,
		countersign: input.countersign === true,
		processingProfile: (FORM_PROCESSING_PROFILES as ReadonlyArray<unknown>).includes(
			input.processingProfile,
		)
			? (input.processingProfile as FormSendSettingsModel['processingProfile'])
			: null,
	};
}

// ─── Sample values (Sample PDF) ────────────────────────────────────────────

export const SAMPLE_CLIENT_NAME = 'Sample Customer LLC';

/**
 * A small grayscale PNG (a pen-stroke squiggle) used as the sample signature. Real signatures are drawn
 * on the pad; the sample only has to look like one on paper.
 */
export const SAMPLE_SIGNATURE_DATA_URL = `${FORM_SIGNATURE_DATA_URL_PREFIX}${[
	'iVBORw0KGgoAAAANSUhEUgAAAPAAAAA8CAAAAAB9FGiBAAACIElEQVR42u2aa47DIAyEfYi5/1VZ7VZqCRDqGUPTVZ',
	'wfrdSW4I/gwY9audllCZzACZzACZzACZzACXwzYAD/EVg2G9hFbJt5RbPvBvw3ag+xbeVVzUb1utiNtgK/sIWVUtdq',
	'AbDshzJw8856EWLA/R3AmQ0ZGOvdyPg18yoRuh3CDuQf8WOCKHBjqVN70bmEV7ShOxPeLpTRa/ZrtMdw9Pegl6pguf',
	'ebd83g3jSDOV+s9EDKG1DCwA3oc2IwZtc6AHak5EbnPzb/LXBYaDBm10JHL9Vzg6B4n063v9zAR9+tJ+WAiVMdo0MC',
	'7N5oDPcDn0bGeqTLARfCG3CyNUXgRjigUpHAxb2rRs7fqZ3JDwN7RmLV3niehVPRar6WTdsD/OZLlJHcTVW6lcKvY5',
	'rF+XDlETb7CT6/30HlbbXBgCtTtNkmYOXl44pWHZUjCR99Zp0IQ1PFkBCLh/QrRveXC20k5Gowpcf8YhjmTPrPgDty',
	'Oj/wBiVQQ4vTzMK9xKal0YNQrtYPIbN46AeXSilp5AkwO/shtYCeH2ytDkwiLXZ21Mkjm+NTCWCk/jMLLcnZD96kFj',
	'XUStkSYO/smOdiG8tW4gOOFuJ7nwVXwy1qDRcXAUNsEQaK1pE+TLzVovbLAt0URB7wEuBA3QNaNyVQbwk30/SWtzw4',
	'1Dy+7i8PsW55uROwrliXAu/738q3Al9zJXACJ3ACJ3ACJ3ACJ/Cu6we5pK0AUFz2mwAAAABJRU5ErkJggg==',
].join('')}`;

const SAMPLE_BY_SOURCE: Readonly<Record<string, string>> = {
	'client.name': SAMPLE_CLIENT_NAME,
	'client.legalName': SAMPLE_CLIENT_NAME,
	'client.accountNumber': '100245',
	'client.phone': '(313) 555-0142',
	'client.email': 'orders@samplecustomer.com',
	'client.businessType': 'Convenience store',
	'client.hours': 'Mon–Sat 7 AM–10 PM, Sun 9 AM–8 PM',
	'shipping.address1': '1200 Woodward Ave',
	'shipping.address2': 'Suite 4',
	'shipping.city': 'Detroit',
	'shipping.state': 'MI',
	'shipping.zip': '48226',
	'shipping.county': 'Wayne',
	'billing.name': SAMPLE_CLIENT_NAME,
	'billing.address1': 'PO Box 3100',
	'billing.address2': '',
	'billing.city': 'Detroit',
	'billing.state': 'MI',
	'billing.zip': '48231',
	'billing.phone': '(313) 555-0199',
	'contact.name': 'Jordan Sample',
	'contact.title': 'Owner',
	'contact.email': 'jordan@samplecustomer.com',
	'contact.phone': '(313) 555-0177',
};

export interface SampleFormAnswersOptionsModel {
	/** `YYYY-MM-DD`, the sample's date. */
	today: string;
	/** The choices worked out for computed dropdowns, so a sample picks a real one. */
	choices?: Readonly<Record<string, ReadonlyArray<{ value: string }>>>;
	/** The label shown for a sample payment method. */
	paymentAnswer?: string;
	/** Real ids for a table's item and model cells, so a sample names real products. */
	itemIds?: ReadonlyArray<number>;
	modelIds?: ReadonlyArray<number>;
}

/**
 * PD-13768 — a value for every field of a form, by type, for the Sample PDF: a sample
 * client ("Sample Customer LLC"), addresses, dates, a sample signature, a payment method
 * and table rows. Nothing is saved; the PDF is drawn by the same renderer as a real
 * submission so it is a faithful sample of any form.
 */
export function sampleFormAnswers(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	options: SampleFormAnswersOptionsModel,
): Record<string, FormAnswerValue> {
	const answers: Record<string, FormAnswerValue> = {};
	for (const field of fields) {
		const value = sampleValue(field, options);
		if (value !== undefined) {
			answers[field.fieldKey] = value;
		}
	}
	return answers;
}

function sampleValue(
	field: FormFieldDefinitionModel,
	options: SampleFormAnswersOptionsModel,
): FormAnswerValue | undefined {
	const bySource =
		field.valueSource != null ? SAMPLE_BY_SOURCE[field.valueSource] : undefined;
	const choices =
		options.choices?.[field.fieldKey] ?? field.selectOptions ?? [];
	switch (field.fieldType) {
		case 'consent':
		case 'boolean':
			return true;
		case 'signature':
			return SAMPLE_SIGNATURE_DATA_URL;
		case 'date':
			return options.today;
		case 'datetime':
			return sampleDateTime(field, options.today);
		case 'number':
			return 2;
		case 'currency':
			return 4.99;
		case 'email':
			return bySource ?? 'jordan@samplecustomer.com';
		case 'phone':
			return bySource ?? '(313) 555-0177';
		case 'url':
			return 'https://samplecustomer.com';
		case 'select':
		case 'teamMember':
			// A choice prefilled from the client (a state) uses the sample client's value.
			return (
				(bySource != null && choices.some((choice) => choice.value === bySource)
					? bySource
					: undefined) ??
				choices[0]?.value ??
				undefined
			);
		case 'multiselect':
			return choices.slice(0, 2).map((choice) => choice.value);
		case 'paymentMethod':
			return options.paymentAnswer ?? formPaymentMethodAnswer(0);
		case 'table':
			return sampleTableRows(field, options);
		case 'file':
		case 'hidden':
			return undefined;
		default:
			if (field.valueSource === FORM_SIGNING_DATE_SOURCE) {
				return options.today;
			}
			if (bySource != null) {
				return bySource;
			}
			if (field.identity === 'full_name' || field.fieldOptions?.role === 'signerName') {
				return 'Jordan Sample';
			}
			if (field.fieldOptions?.role === 'signerTitle') {
				return 'Owner';
			}
			if (field.identity === 'company') {
				return SAMPLE_CLIENT_NAME;
			}
			return field.fieldType === 'textarea'
				? 'Sample notes.'
				: `Sample ${field.adminLabel ?? field.label}`.slice(0, 120);
	}
}

function sampleTableRows(
	field: FormFieldDefinitionModel,
	options: SampleFormAnswersOptionsModel,
): Array<FormTableRowModel> {
	const columns = field.fieldOptions?.table?.columns ?? [];
	const itemIds = field.fieldOptions?.table?.itemIds ?? [];
	const anyItems = options.itemIds ?? [];
	const anyModels = options.modelIds ?? [];
	const rowCount = itemIds.length > 0 ? itemIds.length : 2;
	return Array.from({ length: rowCount }, (_, index) => {
		const row: FormTableRowModel = {};
		for (const column of columns) {
			switch (column.kind) {
				case 'number':
					row[column.key] = String(index + 1);
					break;
				case 'currency':
					row[column.key] = (4.99 + index).toFixed(2);
					break;
				case 'item': {
					const id = itemIds[index] ?? anyItems[index % Math.max(anyItems.length, 1)];
					if (id != null) {
						row[column.key] = String(id);
					}
					break;
				}
				case 'model': {
					const id = anyModels[index % Math.max(anyModels.length, 1)];
					if (id != null) {
						row[column.key] = String(id);
					}
					break;
				}
				default:
					row[column.key] = `Sample ${column.label} ${String(index + 1)}`;
			}
		}
		return row;
	});
}

/** An event's sample times follow each other: drop-off, start, end, pick-up. */
function sampleDateTime(field: FormFieldDefinitionModel, today: string): string {
	const nextDay = addDaysToYmd(today, 1);
	switch (field.fieldOptions?.role) {
		case 'dropOffAt':
			return `${today}T07:00`;
		case 'eventStart':
			return `${today}T10:00`;
		case 'eventEnd':
			return `${nextDay}T18:00`;
		case 'pickUpAt':
			return `${nextDay}T20:00`;
		default:
			return `${today}T10:00`;
	}
}

function addDaysToYmd(ymd: string, days: number): string {
	const [year = 0, month = 1, day = 1] = ymd.split('-').map(Number);
	const date = new Date(Date.UTC(year, month - 1, day + days));
	return date.toISOString().slice(0, 10);
}

/** Whether a stored default is the "today" token (resolved when the form is sent). */
export function isTodayDefault(value: string | null | undefined): boolean {
	return value === FORM_DEFAULT_TODAY;
}

// ─── Agreement terms (Settings → Agreements) ───────────────────────────────

/** Settings → Agreements (`forms.agreements`), as the registry's schema reads it. */
export type AgreementSettingsModel = AgreementSettingsValue;

/** A term length's choice value: its months, as text. */
export function termLengthChoiceValue(months: number): string {
	return String(months);
}

/** `YYYY-MM-DD` plus whole months, clamped to the month's last day (Jan 31 + 1 = Feb 28). */
export function addMonthsYmd(ymd: string, months: number): string {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
	if (match == null) {
		throw new Error(`Not a date: ${ymd}`);
	}
	const year = Number(match[1]);
	const month = Number(match[2]) - 1 + months;
	const day = Number(match[3]);
	const targetYear = year + Math.floor(month / 12);
	const targetMonth = ((month % 12) + 12) % 12;
	const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
	const pad = (value: number): string => String(value).padStart(2, '0');
	return `${String(targetYear)}-${pad(targetMonth + 1)}-${pad(Math.min(day, lastDay))}`;
}

export interface AgreementTermDatesModel {
	endDate: string | null;
	earlyRemovalUntil: string | null;
}

/** An agreement's end date (none for month to month) and early-removal window end. */
export function agreementTermDates(
	startDate: string,
	termMonths: number,
	earlyRemovalMonths: number | null,
): AgreementTermDatesModel {
	return {
		endDate: termMonths > 0 ? addMonthsYmd(startDate, termMonths) : null,
		earlyRemovalUntil:
			earlyRemovalMonths != null && earlyRemovalMonths > 0
				? addMonthsYmd(startDate, earlyRemovalMonths)
				: null,
	};
}

export interface AgreementStatusModel {
	/** "Agreement runs to 10/1/27 — 11 months left", or "Month-to-month agreement". */
	termText: string;
	inTerm: boolean;
	/** "$500 early-removal fee applies until 3/18/28", when still inside the window. */
	earlyRemovalText: string | null;
	earlyRemovalApplies: boolean;
}

/**
 * `2028-04-08` → `4/8/28`: the app's date style (two-digit year, owner 2026-09-07) for
 * dates staff read on screen. The signed document itself keeps the four-digit form a
 * person writes on an agreement (`formatYmd` in crm-api's sent-form-display).
 */
export function formatAgreementYmd(ymd: string): string {
	const match = /^\d{2}(\d{2})-(\d{2})-(\d{2})$/.exec(ymd);
	return match != null
		? `${String(Number(match[2]))}/${String(Number(match[3]))}/${match[1] ?? ''}`
		: ymd;
}

/**
 * A local date and time from a sent form's date-time answer
 * (`YYYY-MM-DDTHH:mm`, or with a space) as M/D/YY h:mm AM, the app's date
 * style. A date-only value is shown as M/D/YY; anything else is returned as is.
 */
export function formatAgreementDateTime(value: string): string {
	const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/.exec(value);
	if (match == null) {
		return formatAgreementYmd(value);
	}
	const hours = Number(match[2]);
	const hour12 = hours % 12 === 0 ? 12 : hours % 12;
	return `${formatAgreementYmd(match[1] ?? '')} ${String(hour12)}:${match[3] ?? '00'} ${hours < 12 ? 'AM' : 'PM'}`;
}

/** Whole months from one date to a later one (a part month counts as one left). */
function monthsLeft(today: string, until: string): number {
	const [ty = 0, tm = 0, td = 0] = today.split('-').map(Number);
	const [uy = 0, um = 0, ud = 0] = until.split('-').map(Number);
	let months = (uy - ty) * 12 + (um - tm);
	if (ud > td) {
		months += 1;
	}
	return Math.max(months, 0);
}

/**
 * PD-13768 — what staff see where they schedule a pick-up of a client's merchandisers:
 * whether the client is still inside its agreement term, and inside the early-removal
 * window. Information only (owner, 2026-10-08): fees stay at staff's discretion.
 */
export function describeAgreementStatus(
	today: string,
	agreement: {
		startDate: string;
		termLabel: string;
		endDate: string | null;
		earlyRemovalUntil: string | null;
	},
	earlyRemovalFeeText: string | null = null,
): AgreementStatusModel {
	const inTerm = agreement.endDate == null || agreement.endDate > today;
	let termText: string;
	if (agreement.endDate == null) {
		termText = `Month-to-month agreement since ${formatAgreementYmd(agreement.startDate)}`;
	} else if (inTerm) {
		const left = monthsLeft(today, agreement.endDate);
		termText = `Agreement (${agreement.termLabel}) runs to ${formatAgreementYmd(agreement.endDate)} — ${String(left)} ${left === 1 ? 'month' : 'months'} left`;
	} else {
		termText = `Agreement (${agreement.termLabel}) ended ${formatAgreementYmd(agreement.endDate)}`;
	}
	const earlyRemovalApplies =
		agreement.earlyRemovalUntil != null && agreement.earlyRemovalUntil > today;
	return {
		termText,
		inTerm,
		earlyRemovalApplies,
		earlyRemovalText: earlyRemovalApplies
			? `${earlyRemovalFeeText ?? 'The early-removal fee'} applies until ${formatAgreementYmd(agreement.earlyRemovalUntil ?? '')}`
			: null,
	};
}

// ─── Calculations ──────────────────────────────────────────────────────────

/**
 * PD-13768 — an event's total price estimate: every table row's count times its price
 * (a row with a price and no count counts once), across the form's tables. Shown only;
 * the orders are priced by the normal rules when they are created.
 */
export function calculateFormTotal(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	values: Readonly<Record<string, FormAnswerValue>>,
): number {
	let total = 0;
	for (const field of fields) {
		if (field.fieldType !== 'table') {
			continue;
		}
		const rows = values[field.fieldKey];
		if (!isFormTableAnswer(rows)) {
			continue;
		}
		const columns = field.fieldOptions?.table?.columns ?? [];
		const countKey = columns.find((column) => column.kind === 'number')?.key;
		const priceKey = columns.find((column) => column.kind === 'currency')?.key;
		if (priceKey == null) {
			continue;
		}
		for (const row of rows) {
			const price = Number(row[priceKey] ?? '');
			if (!Number.isFinite(price)) {
				continue;
			}
			const countText = countKey != null ? row[countKey] : undefined;
			const count = countText != null && countText !== '' ? Number(countText) : 1;
			if (Number.isFinite(count)) {
				total += count * price;
			}
		}
	}
	return Math.round(total * 100) / 100;
}

/** Every calculated field's value from the values so far. */
export function applyFormCalculations(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	values: Record<string, FormAnswerValue>,
): Record<string, FormAnswerValue> {
	const next = { ...values };
	for (const field of fields) {
		if (field.fieldOptions?.calculation === 'event_total') {
			next[field.fieldKey] = calculateFormTotal(fields, values);
		}
	}
	return next;
}

// ─── Field roles (what a processing profile reads) ─────────────────────────

/**
 * PD-13768 — the roles a processing profile reads from a sent form's answers
 * (`fieldOptions.role`), so the processing screen finds the agreement type, the effective
 * date or the equipment rows whatever each field's key is.
 */
export const SENT_FORM_ROLES = {
	agreementType: 'agreementType',
	effectiveDate: 'effectiveDate',
	equipment: 'equipment',
	pricing: 'pricing',
	paymentMethod: 'paymentMethod',
	billingTerms: 'billingTerms',
	termLength: 'termLength',
	signerName: 'signerName',
	signerTitle: 'signerTitle',
	eventType: 'eventType',
	eventProducts: 'eventProducts',
	eventStart: 'eventStart',
	eventEnd: 'eventEnd',
	dropOffAt: 'dropOffAt',
	pickUpAt: 'pickUpAt',
	rental: 'rental',
	notes: 'notes',
} as const;

/** The Customer Agreement's agreement types (its first question). */
export const AGREEMENT_TYPES = ['new_account', 'service_change', 'account_change'] as const;

export type AgreementTypeModel = (typeof AGREEMENT_TYPES)[number];

/** The field carrying a role, if the form has one. */
export function fieldWithRole<TField extends Pick<FormFieldDefinitionModel, 'fieldOptions'>>(
	fields: ReadonlyArray<TField>,
	role: string,
): TField | undefined {
	return fields.find((field) => field.fieldOptions?.role === role);
}

/**
 * PD-13768 — an event's times must make sense: the drop-off at or before the event
 * starts, the event ending after it starts, and the pick-up at or after it ends. Values
 * are local `YYYY-MM-DDTHH:mm`. Returns the problems by role.
 */
export function checkEventTimes(times: {
	eventStart?: string | null;
	eventEnd?: string | null;
	dropOffAt?: string | null;
	pickUpAt?: string | null;
}): Record<string, string> {
	const problems: Record<string, string> = {};
	const { eventStart, eventEnd, dropOffAt, pickUpAt } = times;
	if (eventStart != null && eventEnd != null && eventEnd < eventStart) {
		problems[SENT_FORM_ROLES.eventEnd] = 'The event cannot end before it starts.';
	}
	if (dropOffAt != null && eventStart != null && dropOffAt > eventStart) {
		problems[SENT_FORM_ROLES.dropOffAt] = 'The drop-off must be at or before the event starts.';
	}
	if (pickUpAt != null && eventEnd != null && pickUpAt < eventEnd) {
		problems[SENT_FORM_ROLES.pickUpAt] = 'The pick-up must be at or after the event ends.';
	}
	if (dropOffAt != null && pickUpAt != null && pickUpAt < dropOffAt) {
		problems[SENT_FORM_ROLES.pickUpAt] = 'The pick-up cannot be before the drop-off.';
	}
	return problems;
}
